/**
 * Job-description field vocabulary, derived from research/jd-fields.json.
 *
 * Where a value also appears in schema.org/JobPosting the exact schema token
 * is stored and a friendly label is rendered from it, so structured data never
 * has to be translated at emit time.
 */
/** schema.org employmentType tokens. A listing may carry more than one. */
export const EMPLOYMENT_TYPES = [
    'FULL_TIME',
    'PART_TIME',
    'CONTRACTOR',
    'TEMPORARY',
    'INTERN',
    'VOLUNTEER',
    'PER_DIEM',
    'OTHER',
];
export const EMPLOYMENT_TYPE_LABELS = {
    FULL_TIME: 'Full time',
    PART_TIME: 'Part time',
    CONTRACTOR: 'Contract',
    TEMPORARY: 'Temporary',
    INTERN: 'Internship',
    VOLUNTEER: 'Volunteer',
    PER_DIEM: 'Per diem',
    OTHER: 'Other',
};
/** Where the work happens. Kept strictly separate from the location list. */
export const WORKPLACE_TYPES = ['ONSITE', 'HYBRID', 'REMOTE'];
export const WORKPLACE_TYPE_LABELS = {
    ONSITE: 'On site',
    HYBRID: 'Hybrid',
    REMOTE: 'Remote',
};
export const SENIORITY_LEVELS = [
    'INTERNSHIP',
    'ENTRY',
    'JUNIOR',
    'MID',
    'SENIOR',
    'STAFF_PRINCIPAL',
    'MANAGER',
    'DIRECTOR',
    'VP',
    'EXECUTIVE',
];
export const SENIORITY_LABELS = {
    INTERNSHIP: 'Internship',
    ENTRY: 'Entry level',
    JUNIOR: 'Junior',
    MID: 'Mid level',
    SENIOR: 'Senior',
    STAFF_PRINCIPAL: 'Staff / Principal',
    MANAGER: 'Manager',
    DIRECTOR: 'Director',
    VP: 'VP',
    EXECUTIVE: 'Executive',
};
/** schema.org QuantitativeValue.unitText for a salary figure. */
export const COMPENSATION_PERIODS = ['HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR'];
export const COMPENSATION_PERIOD_LABELS = {
    HOUR: 'per hour',
    DAY: 'per day',
    WEEK: 'per week',
    MONTH: 'per month',
    YEAR: 'per year',
};
export const VISA_SPONSORSHIP_OPTIONS = [
    'YES',
    'NO',
    'CASE_BY_CASE',
    'UNSPECIFIED',
];
export const VISA_SPONSORSHIP_LABELS = {
    YES: 'Visa sponsorship available',
    NO: 'No visa sponsorship',
    CASE_BY_CASE: 'Sponsorship considered case by case',
    UNSPECIFIED: 'Sponsorship not stated',
};
/**
 * Lifecycle of a listing.
 *
 * Status is the two-by-two of "has it been paid for?" and "does it have its
 * content yet?", and nothing else records either fact:
 *
 * ```
 *                  content: absent          content: complete
 * paid: no      PENDING_CONTENT   ──────►  PENDING_PAYMENT
 *                     │                          │
 *                     ▼                          ▼
 * paid: yes  PAID_AWAITING_CONTENT ──────►     ACTIVE ──────► EXPIRED / FILLED / …
 * ```
 *
 * The product's own flow is pay first: a checkout mints a contentless shell
 * (`PENDING_CONTENT`), the webhook marks it `PAID_AWAITING_CONTENT`, and
 * submitting the posting details publishes it. `PENDING_PAYMENT` is the
 * mirror image — content typed before the money landed — which is how every
 * listing created under the older listing-first flow reached the table, and
 * those rows are still completable through the same checkout they always used.
 *
 * Only ACTIVE listings render and rank, and a listing reaches ACTIVE only when
 * both halves are present: money and content.
 */
export const JOB_STATUSES = [
    'DRAFT',
    'PENDING_CONTENT',
    'PENDING_PAYMENT',
    'PAID_AWAITING_CONTENT',
    'IN_REVIEW',
    'ACTIVE',
    'EXPIRED',
    'FILLED',
    'REJECTED',
    'REFUNDED',
];
export const JOB_STATUS_LABELS = {
    DRAFT: 'Draft',
    PENDING_CONTENT: 'Reserved — payment pending',
    PENDING_PAYMENT: 'Draft — awaiting payment',
    PAID_AWAITING_CONTENT: 'Paid — finish your listing',
    IN_REVIEW: 'In review',
    ACTIVE: 'Live',
    EXPIRED: 'Expired',
    FILLED: 'Filled',
    REJECTED: 'Not approved',
    REFUNDED: 'Refunded',
};
/** Only these statuses appear on a leaderboard. */
export const RANKABLE_JOB_STATUSES = ['ACTIVE'];
/**
 * The statuses a listing holds before it has been published.
 * A listing in one of these belongs to its owner's dashboard and appears
 * nowhere on the public site.
 */
export const UNPUBLISHED_JOB_STATUSES = [
    'DRAFT',
    'PENDING_CONTENT',
    'PENDING_PAYMENT',
    'PAID_AWAITING_CONTENT',
];
/**
 * The statuses in which a listing has no content at all — no title, no
 * description, no category. Only the reserving checkout creates one, and only
 * the content submission takes it out of one. Nothing may read a content field
 * off a listing in one of these states, which is what `hasListingContent`
 * exists to enforce.
 */
export const CONTENTLESS_JOB_STATUSES = [
    'PENDING_CONTENT',
    'PAID_AWAITING_CONTENT',
];
/** The status a listing is created in: reserved, awaiting its payment. */
export const INITIAL_JOB_STATUS = 'PENDING_CONTENT';
/**
 * Whether a listing has been published — that is, whether it has been paid for
 * AND its posting details have been submitted. Posting is not free: money is
 * what reserves a listing, and the details are what put it in front of anyone.
 */
export function isPublishedListing(listing) {
    if (listing.totalPaidCents <= 0)
        return false;
    return !UNPUBLISHED_JOB_STATUSES.includes(listing.status);
}
/**
 * Whether a listing is waiting on the payment that will let it publish — the
 * state the dashboard renders with a "complete your bid" call to action.
 */
export function isAwaitingFirstPayment(listing) {
    return listing.totalPaidCents <= 0 && UNPUBLISHED_JOB_STATUSES.includes(listing.status);
}
/**
 * Whether a listing is still a shell: reserved, with no posting details on it.
 * True whether or not the reservation has been paid for, which is why the
 * dashboard asks `isPaidAwaitingContent` as well before choosing its words.
 */
export function isAwaitingContent(listing) {
    return CONTENTLESS_JOB_STATUSES.includes(listing.status);
}
/**
 * The dashboard's "Paid — finish your listing" row: the money is taken and the
 * listing publishes the moment its details are submitted.
 *
 * `isAwaitingFirstPayment` is deliberately false here — the payment has landed
 * — and `isPublishedListing` is false too, so this state needs a predicate of
 * its own or it falls through every branch the dashboard has.
 */
export function isPaidAwaitingContent(listing) {
    return listing.status === 'PAID_AWAITING_CONTENT';
}
/**
 * The one narrowing gate between a stored listing and a listing with content.
 * Every public surface reads a `Job`; this is how a dashboard row, which may
 * be either, proves it has one.
 */
export function hasListingContent(listing) {
    return !CONTENTLESS_JOB_STATUSES.includes(listing.status);
}
export const COMPANY_SIZE_BANDS = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5000+',
];
export const FUNDING_STAGES = [
    'BOOTSTRAPPED',
    'PRE_SEED',
    'SEED',
    'SERIES_A',
    'SERIES_B',
    'SERIES_C',
    'SERIES_D_PLUS',
    'PUBLIC',
    'NONPROFIT',
    'GOVERNMENT',
    'UNSPECIFIED',
];
export const FUNDING_STAGE_LABELS = {
    BOOTSTRAPPED: 'Bootstrapped',
    PRE_SEED: 'Pre-seed',
    SEED: 'Seed',
    SERIES_A: 'Series A',
    SERIES_B: 'Series B',
    SERIES_C: 'Series C',
    SERIES_D_PLUS: 'Series D or later',
    PUBLIC: 'Public company',
    NONPROFIT: 'Non-profit',
    GOVERNMENT: 'Government',
    UNSPECIFIED: 'Not stated',
};
/** Applicant location requirement kinds, used when a listing is remote. */
export const LOCATION_REQUIREMENT_TYPES = ['COUNTRY', 'STATE', 'REGION'];
/** Benefits normalized into a filterable vocabulary; free text may ride alongside. */
export const COMMON_BENEFITS = [
    'health-insurance',
    'dental-insurance',
    'vision-insurance',
    'retirement-401k',
    'equity',
    'unlimited-pto',
    'four-day-week',
    'remote-stipend',
    'learning-budget',
    'parental-leave',
    'relocation-assistance',
    'commuter-benefits',
    'wellness-stipend',
    'home-office-budget',
    'visa-support',
];
/** Field limits enforced at the API boundary and mirrored in the form UI. */
export const JD_LIMITS = {
    titleMaxLength: 120,
    summaryMaxLength: 280,
    maxLocations: 10,
    maxBenefits: 15,
    maxSkills: 20,
    maxTags: 10,
    companyBlurbMaxLength: 500,
};
/** A listing runs for 60 days from its last renewal. Renewal is free. */
export const LISTING_LIFETIME_DAYS = 60;
/** A listing may never be dated further out than 180 days. */
export const LISTING_MAX_LIFETIME_DAYS = 180;
/** Application hosts we accept without a verified company domain. */
export const KNOWN_ATS_HOSTS = [
    'greenhouse.io',
    'boards.greenhouse.io',
    'job-boards.greenhouse.io',
    'lever.co',
    'jobs.lever.co',
    'ashbyhq.com',
    'jobs.ashbyhq.com',
    'myworkdayjobs.com',
    'workable.com',
    'apply.workable.com',
    'smartrecruiters.com',
    'jobs.smartrecruiters.com',
    'bamboohr.com',
    'recruitee.com',
    'teamtailor.com',
    'rippling.com',
    'breezy.hr',
    'jazzhr.com',
    'icims.com',
    'taleo.net',
    'successfactors.com',
];
//# sourceMappingURL=jd-fields.js.map